/* global document, Image, window */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import supportsWebP from 'supports-webp';
import isEqual from 'lodash/isEqual';
import zipWith from 'lodash/zipWith';

import Activity from 'brastrap/common/activity/Activity';

const isInBounds = (coordinates, upperBounds) => {
  let flag = true;
  coordinates.forEach((marker, i) => {
    if (marker <= 0 || marker >= upperBounds[i]) {
      flag = false;
    }
  });
  return flag;
};

const nearestInBoundsPosition = (loc, bounds) =>
  loc.map((p, i) => Math.min(p, bounds[i]));

class ZoomableImage extends Component {
  /**
   * Sets initial size of images, and zoom level into state
   * @param {*} props
   */
  constructor(props) {
    super(props);

    // We don't have the image size from react measure yet,
    // so setting an inital value of 1/2 the expected desktop size
    const [width, height] =
      props.figureDimensions && props.figureDimensions.length === 2
        ? props.figureDimensions
        : [550 / 2, 704 / 2];

    this.state = {
      baseImageSize: [width, height],
      baseImageSizeReceived: false,
      zoomedImageSize: [width, height],
      zoomedImageSizeReceived: false,
      zoomLevel: 0.5,
      defaultZoomLevel: 1.2,
      viewWindowPosition: [0, 0],
      dragFocalPoint: [0, 0],
      lastClickToZoomPosition: [-1, -1],
      isCalculatingZoom: false,
      isDragging: false,
      calcMovementFromDrag: false,
      pageClickHandlersAreAdded: false,
      zoomedImageTransitionClassName:
        'c-product-gallery-zoom__transition-enabled',
      bodyClickHandlers: [
        ['click', this.props.endZoom],
        ['touchstart', this.checkForTap],
        ['touchend', this.onTouchEnd],
      ],
      useWebP: undefined,
    };
  }

  /**
   * Set webP compatibility into state
   */
  async componentWillMount() {
    if (await supportsWebP) {
      this.setState(
        {
          useWebP: true,
        },
        // setZoomedImageSize needs to be set only once useWebP state has been updated,
        // otherwise useWebP will be undefined and the browser will download the jpegs.
        () => this.setZoomedImageSize(this.props.image.zoom)
      );
    } else {
      this.setState(
        {
          useWebP: false,
        },
        () => this.setZoomedImageSize(this.props.image.zoom)
      );
    }
  }

  /**
   * This is all a workaround to handle chrome disabling e.preventDefault()
   * unless you specify to enable it. Unfortunately this cannot be done through
   * React synthetic events
   */
  componentDidMount() {
    let passiveSupported = false;
    try {
      const options = Object.defineProperty({}, 'passive', {
        get: () => {
          passiveSupported = true;
        },
      });
      document.querySelector('body').addEventListener('test', null, options);
    } catch (err) {
      passiveSupported = false;
    }
    const passive = passiveSupported ? { passive: false } : false;
    if (this.zoomContainer) {
      this.zoomContainer.addEventListener(
        'touchmove',
        this.calcBackgroundOffsetFromDrag,
        passive
      );
      this.zoomContainer.addEventListener(
        'touchstart',
        this.onZoomWindowTouchStart,
        passive
      );
      this.zoomContainer.addEventListener('touchend', this.onTouchEnd, passive);
    }
  }

  /**
   * @param {Object} nextProps
   */
  componentWillReceiveProps({
    figureDimensions,
    initialFocalPoint,
    stackFactor,
    isTouchDevice,
  }) {
    if (figureDimensions) {
      // If we have received the exact base image size, set it in state
      // and if we also have the zoomed image size, set the starting zoom level.
      // Otherwise, initial zoom will be set when we know the zoomed image size
      if (!isEqual(this.state.baseImageSize, figureDimensions)) {
        const update = {
          baseImageSize: figureDimensions,
          baseImageSizeReceived: true,
        };
        if (this.state.zoomedImageSizeReceived) {
          update.initialZoomLevel =
            figureDimensions[0] / this.state.zoomedImageSize[0];
        }
        this.setState(update);
      }
    }

    // Center the focus on the click-to-zoom click position
    if (
      initialFocalPoint &&
      !isEqual(this.state.lastClickToZoomPosition, initialFocalPoint)
    ) {
      if (!isEqual(this.state.viewWindowPosition, initialFocalPoint)) {
        const { zoomedImageSize, baseImageSize, defaultZoomLevel } = this.state;

        let startingFocus = isTouchDevice
          ? initialFocalPoint.map(
              p =>
                p * defaultZoomLevel * (zoomedImageSize[0] / baseImageSize[0])
            )
          : initialFocalPoint;

        if (isTouchDevice && !isInBounds(startingFocus, zoomedImageSize)) {
          startingFocus = nearestInBoundsPosition(
            startingFocus,
            zoomedImageSize
          );
        }
        this.setState({
          viewWindowPosition: startingFocus,
          lastClickToZoomPosition: initialFocalPoint,
          calcMovementFromDrag: isTouchDevice,
        });
      }
    }

    // StackFactor controls z-index, and is 1 when the zoom component is visible, 0 when it is not
    if (stackFactor === 1 && !this.state.pageClickHandlersAreAdded) {
      this.addBodyClickHandlers();
      this.setState({
        zoomLevel: this.state.defaultZoomLevel,
        pageClickHandlersAreAdded: true,
      });

      // Turning off transitions so there is no delay between mousemove and the background
      // image repositioning
      setTimeout(
        () =>
          this.setState({
            zoomedImageTransitionClassName:
              'c-product-gallery-zoom__transition-disabled',
          }),
        this.props.zoomTransitionTime
      );
    } else if (stackFactor === 0 && this.state.pageClickHandlersAreAdded) {
      // Transitioning out of zoom view
      this.removeBodyClickHandlers();
      this.setState({
        zoomLevel: this.state.initialZoomLevel,
        pageClickHandlersAreAdded: false,
        zoomedImageTransitionClassName:
          'c-product-gallery-zoom__transition-enabled',
      });
    }
  }

  /**
   * Make sure we don't leave any garbage behind
   */
  componentWillUnmount() {
    this.removeBodyClickHandlers();
  }

  onZoomWindowTouchStart = e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.targetTouches) {
      this.setState({
        isDragging: true,
        previousDragPosition: [
          e.targetTouches[0].clientX,
          e.targetTouches[0].clientY,
        ],
      });
      this.checkForTap();
    }
  };

  onTouchEnd = e => {
    // If this was a tap, not a drag, close the zoom view
    if (this.state.listeningForSingleClick) {
      this.props.endZoom();
    } else {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState({ isDragging: false });
  };

  setZoomedImageSize = src => {
    const image = new Image();
    image.onload = () => {
      const update = {
        zoomedImageSize: [image.width, image.height],
        zoomedImageSizeReceived: true,
      };
      if (this.state.baseImageSizeReceived) {
        update.initialZoomLevel = this.state.baseImageSize[0] / image.width;
      }
      this.setState(update);
    };
    image.setAttribute('src', this.state.useWebP ? `${src}&fm=webp` : src);
  };

  /**
   * Close zoom view following a click or tap anywhere on the page
   */
  addBodyClickHandlers = () => {
    if (document && document.querySelector) {
      this.state.bodyClickHandlers.forEach(handler => {
        document.querySelector('body').addEventListener(...handler);
      });
    }
  };

  removeBodyClickHandlers = () => {
    if (document && document.querySelector) {
      this.state.bodyClickHandlers.forEach(handler => {
        document.querySelector('body').removeEventListener(...handler);
      });
    }
  };

  calcBackgroundOffsetFromMouse = e => {
    if (!this.state.isDragging && e.nativeEvent) {
      if (this.state.calcMovementFromDrag)
        this.setState({ calcMovementFromDrag: false });
      const { offsetX, offsetY } = e.nativeEvent;
      const viewWindowPosition = [offsetX, offsetY];
      window.requestAnimationFrame(() => {
        this.setState({ viewWindowPosition });
      });
    }
  };

  calcBackgroundOffsetFromDrag = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!this.state.calcMovementFromDrag)
      this.setState({ calcMovementFromDrag: true });

    const {
      previousDragPosition,
      viewWindowPosition,
      zoomedImageSize,
    } = this.state;

    if (e.targetTouches) {
      const currentDragPosition = [
        e.targetTouches[0].clientX,
        e.targetTouches[0].clientY,
      ];

      // Translate the view window focal point by the drag distance
      const newFocus = zipWith(
        viewWindowPosition,
        currentDragPosition,
        previousDragPosition,
        (f, c, p) => f + (p - c)
      );

      if (isInBounds(newFocus, zoomedImageSize)) {
        window.requestAnimationFrame(() => {
          this.setState({
            viewWindowPosition: newFocus,
            previousDragPosition: currentDragPosition,
          });
        });
      }
    }
  };

  checkForTap = () => {
    this.setState({ listeningForSingleClick: true });
    setTimeout(() => this.setState({ listeningForSingleClick: false }), 250);
  };

  pauseZoom = () => this.setState({ isCalculatingZoom: true });

  resumeZoom = () => this.setState({ isCalculatingZoom: false });

  handleZoom = e => {
    e.preventDefault();
    let { zoomLevel } = this.state;
    let delta;
    if (!this.state.isCalculatingZoom && zoomLevel <= 2 && zoomLevel >= 0) {
      this.pauseZoom();

      if (e.deltaY < 0 && zoomLevel < 2) {
        delta = 0.1;
      } else if (e.deltaY > 0 && zoomLevel > 1) {
        delta = -0.1;
      }

      if (delta) {
        zoomLevel = Math.round((zoomLevel + delta) * 10) / 10;
        this.setState({ zoomLevel }, this.resumeZoom);
      } else {
        this.resumeZoom();
      }
    }
  };

  /**
   * Renders.
   * @returns {*}
   */
  render() {
    const { image, endZoom, stackFactor, displayMap } = this.props;

    const {
      baseImageSize,
      viewWindowPosition,
      zoomedImageSize,
      zoomLevel,
      calcMovementFromDrag,
      useWebP,
    } = this.state;

    const [width, height] = baseImageSize;
    const baseToZoomRatio = width / (zoomedImageSize[0] * zoomLevel);
    const referencePosition = calcMovementFromDrag
      ? zoomedImageSize
      : baseImageSize;
    const viewWindowPercent = viewWindowPosition.map(
      (offset, i) => Math.round((offset / referencePosition[i]) * 1000) / 10
    );

    let backgroundImg = null;
    let thumbnailImg = null;

    // isWebPCompatible is set in componentWillMount, meaning that on first render 'isWebPCompatible'
    // is undefined. The images should only be set once we know whether the browser is webP compatible
    if (useWebP === true) {
      backgroundImg = `url(${image.zoom}&fm=webp)`;
      thumbnailImg = `${image.thumbnail}&fm=webp`;
    } else if (useWebP === false) {
      backgroundImg = `url(${image.zoom})`;
      thumbnailImg = image.thumbnail;
    }

    return (
      <div
        className="c-product-gallery-zoom__background-placeholder"
        onClick={() => !this.state.isDragging && endZoom()}
        style={{ width, height }}
      >
        <div className="c-product-gallery-zoom__loading-note">
          <Activity />
        </div>
        <div
          className="c-product-gallery-zoom__container"
          ref={domNode => {
            this.zoomContainer = domNode;
          }}
          onMouseMove={this.calcBackgroundOffsetFromMouse}
          onWheel={this.handleZoom}
          style={{ width, height }}
        >
          {displayMap && (
            <div
              className="c-product-gallery-zoom__inner-map"
              style={{
                width: width * 0.2,
                height: height * 0.2,
                zIndex: stackFactor * 200,
              }}
            >
              <img
                className="c-product-gallery-zoom__inner-map-image"
                src={thumbnailImg}
                alt="thumbnail"
              />
              <div
                className="c-product-gallery-zoom__inner-map-highlight-box"
                style={{
                  width: `${baseToZoomRatio * 100}%`,
                  height: `${baseToZoomRatio * 100}%`,
                  left: `${viewWindowPercent[0] * (1 - baseToZoomRatio)}%`,
                  top: `${viewWindowPercent[1] * (1 - baseToZoomRatio)}%`,
                }}
              />
            </div>
          )}
          <div
            className={`c-product-gallery-zoom__zoomed-image ${this.state.zoomedImageTransitionClassName}`}
            style={{
              width,
              height,
              backgroundPosition: viewWindowPercent.map(p => `${p}%`).join(' '),
              backgroundSize: `${zoomedImageSize[0] * zoomLevel}px ${
                zoomedImageSize[1] * zoomLevel
              }px`,
              backgroundImage: backgroundImg,
              zIndex: stackFactor * 110,
            }}
          />
        </div>
      </div>
    );
  }
}

const imageShape = {
  alt: PropTypes.string.isRequired,
  id: PropTypes.string,
  thumbnail: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  zoom: PropTypes.string.isRequired,
};

ZoomableImage.propTypes = {
  image: PropTypes.shape(imageShape),
  endZoom: PropTypes.func,
  figureDimensions: PropTypes.array,
  stackFactor: PropTypes.number.isRequired,
  initialFocalPoint: PropTypes.number,
  isTouchDevice: PropTypes.bool,
  zoomTransitionTime: PropTypes.number,
  displayMap: PropTypes.bool,
};

ZoomableImage.defaultProps = {
  endZoom: () => {},
  figureDimensions: [],
  zoomTransitionTime: 300,
  displayMap: true,
};

export default ZoomableImage;
